import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetch, fetchById } from "services/assets.service";
import { fetchAssetsTypes } from "services/assets-types.service";
import { Asset } from "types/Asset";
import { AppDispatch } from "configureStore";

export const ASSETS_PAGE_SIZE = 25;

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

interface AssetsState {
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  errMsg: string;
  collection: Asset[];
  pagination: Pagination | null;
  queryValue: any;
  assetTypes: any;
  filtersValue: any;
}

const initialState: AssetsState = {
  isLoading: true,
  isLoadingMore: false,
  isError: false,
  errMsg: "",
  collection: [],
  pagination: null,
  queryValue: null,
  assetTypes: {},
  filtersValue: {},
};

const AssetsSlice = createSlice({
  name: "assets",
  initialState,
  /* eslint-disable no-param-reassign */
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: AssetsState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
      // Starting a fresh load clears any previous error so the UI can recover.
      if (action.payload) {
        state.isError = false;
        state.errMsg = "";
      }
    },
    setLoadingMore(state: AssetsState, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload;
    },
    setError(state: AssetsState, action: PayloadAction<{ error: string }>) {
      state.isLoading = false;
      state.isLoadingMore = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setData(
      state: AssetsState,
      action: PayloadAction<{ collection: Asset[]; pagination: Pagination | null }>
    ) {
      state.collection = action.payload.collection;
      state.pagination = action.payload.pagination;
      state.isLoading = false;
    },
    appendData(
      state: AssetsState,
      action: PayloadAction<{ collection: Asset[]; pagination: Pagination | null }>
    ) {
      const seen = new Set(state.collection.map((a: any) => a.id));
      const fresh = action.payload.collection.filter(
        (a: any) => !seen.has(a.id)
      );
      state.collection = [...state.collection, ...fresh];
      state.pagination = action.payload.pagination;
      state.isLoadingMore = false;
    },
    // Replace a single already-loaded asset in place (after edit/assign) so the
    // loaded pages and scroll position are preserved. No-op if the asset isn't
    // currently in the list.
    upsertAsset(state: AssetsState, action: PayloadAction<any>) {
      const updated = action.payload;
      if (!updated || !updated.id) return;
      const idx = state.collection.findIndex(
        (a: any) => a.id === updated.id
      );
      if (idx !== -1) {
        state.collection[idx] = updated;
      }
    },
    setFilterValue(state, action) {
      const { key, value } = action.payload;
      state.filtersValue[key] = value;
    },
    setAssetsTypes(state, action) {
      for (let i = 0; i < action.payload.collection.length; i++) {
        const { id, title } = action.payload.collection[i];
        state.assetTypes[id] = title;
      }
    },
  },
});

const { actions, reducer } = AssetsSlice;

export const {
  setDestroy,
  setLoading,
  setLoadingMore,
  setError,
  setData,
  appendData,
  upsertAsset,
  setFilterValue,
  setAssetsTypes,
} = actions;
export default reducer;

// First page (replace). Also (re)loads the asset-type lookup. Called on mount,
// on filter change, and after create/edit to refresh the list.
export const fetchAssets =
  (
    companyId: string,
    filtersValue: any,
    page: number = 1,
    pageSize: number = ASSETS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const data = await fetch(companyId, filtersValue, { page, pageSize });
      dispatch(
        setData({
          collection: data.assetsCollection || [],
          pagination: data.pagination || null,
        })
      );
      const collection = await fetchAssetsTypes(companyId);
      dispatch(setAssetsTypes({ collection }));
    } catch (e) {
      const error = "not_fetch";
      dispatch(setError({ error }));
    } finally {
      dispatch(setLoading(false));
    }
  };

// Subsequent pages (append) for infinite scroll.
export const fetchMoreAssets =
  (
    companyId: string,
    filtersValue: any,
    page: number,
    pageSize: number = ASSETS_PAGE_SIZE
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingMore(true));
    try {
      const data = await fetch(companyId, filtersValue, { page, pageSize });
      dispatch(
        appendData({
          collection: data.assetsCollection || [],
          pagination: data.pagination || null,
        })
      );
    } catch (e) {
      dispatch(setLoadingMore(false));
    }
  };

// Refresh a single asset in place after an edit/assign, keeping the loaded
// pages and scroll position. Uses the fetch-by-id endpoint and maps its
// response to the list-row shape: the list uses a single `latestFileEntity`
// (return file takes precedence over assign file), which fetch-by-id returns
// as separate `returnFileEntity` / `fileEntity`, so we derive it here.
export const refreshAsset =
  (companyId: string, assetId: string) => async (dispatch: AppDispatch) => {
    try {
      const data: any = await fetchById(companyId, assetId);
      if (!data || !data.id) return;
      const updated = {
        id: data.id,
        name: data.name,
        type: data.type,
        brand: data.brand,
        model: data.model,
        serial: data.serial,
        status: data.status,
        assigned_to: data.assigned_to,
        purchase_date: data.purchase_date,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        latestFileEntity: data.returnFileEntity || data.fileEntity || null,
      };
      dispatch(upsertAsset(updated));
    } catch (e) {
      // Best-effort: on failure the row keeps its previously displayed values.
    }
  };
